import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { ArrowLeft, Snowflake, Zap, Refrigerator, House, Printer } from "lucide-react";
import DateInput from "../DateInput";
import { PosizioneFrigoSelect } from "./PosizioneFrigoSelect";
import { PostCreazioneDatiCongelamento } from "./PostCreazioneDatiCongelamento";
import { avvisoConservazione, frigoDellaPosizione, metodoDaFrigo, nomeMetodo } from "./conservazione";

const METODI = [
  { id: "frigo", label: "Frigo", icon: Refrigerator, color: "text-emerald-700 border-emerald-300 bg-emerald-50" },
  { id: "temp_ambiente", label: "Temp. ambiente", icon: House, color: "text-amber-800 border-amber-300 bg-amber-50" },
  { id: "congelatore", label: "Congelatore", icon: Snowflake, color: "text-sky-700 border-sky-300 bg-sky-50" },
  { id: "abbattitore", label: "Abbattitore", icon: Zap, color: "text-indigo-700 border-indigo-300 bg-indigo-50" },
];

const PostCreazioneLottoDialog = ({
  open, onOpenChange, lotto, nomeProdotto, defaultScadenza = "", defaultPosizione = "",
  frigoriferi = [], posizioni = [], saving = false, onConserva, onCongela,
}) => {
  const [step, setStep] = useState("scelta");
  const [posizione, setPosizione] = useState("");
  const [metodo, setMetodo] = useState(null);
  const [dataScadenza, setDataScadenza] = useState("");
  const [formCongela, setFormCongela] = useState({ data: "", mesi: 3, data_scadenza_custom: "", note: "", quantita_parziale: "", solo_parte: false });
  const initializedLottoRef = useRef(null);
  const lottoId = lotto?.id;
  const lottoLocale = lotto?.locale_id;
  const lottoQuantita = lotto?.quantita_preparata;

  useEffect(() => {
    if (!open) { initializedLottoRef.current = null; return; }
    if (!lottoId || initializedLottoRef.current === `${lottoLocale}:${lottoId}`) return;
    initializedLottoRef.current = `${lottoLocale}:${lottoId}`;
    const frigo = frigoDellaPosizione(defaultPosizione, frigoriferi, lottoLocale);
    const suggerito = metodoDaFrigo(frigo);
    setPosizione(defaultPosizione || "");
    setMetodo(suggerito);
    setStep(suggerito ? "dettagli" : "scelta");
    setDataScadenza(defaultScadenza || "");
    setFormCongela({ data: new Date().toISOString().slice(0, 10), mesi: suggerito === "abbattitore" ? 6 : 3,
      data_scadenza_custom: "", note: "", quantita_parziale: lottoQuantita != null ? String(lottoQuantita) : "", solo_parte: false });
  }, [open, lottoId, lottoLocale, lottoQuantita, defaultPosizione, defaultScadenza, frigoriferi]);

  // Il lotto può arrivare prima dell'elenco frigoriferi: completa la scelta
  // automatica quando arriva la tipologia, senza sovrascrivere modifiche manuali.
  useEffect(() => {
    if (!open || !defaultPosizione || posizione !== defaultPosizione || metodo) return;
    const suggerito = metodoDaFrigo(frigoDellaPosizione(posizione, frigoriferi, lottoLocale));
    if (!suggerito) return;
    setMetodo(suggerito);
    setFormCongela(prev => ({ ...prev, mesi: suggerito === "abbattitore" ? 6 : 3 }));
    setStep("dettagli");
  }, [open, defaultPosizione, posizione, metodo, frigoriferi, lottoLocale]);

  const frigo = frigoDellaPosizione(posizione, frigoriferi, lotto?.locale_id);
  const avviso = avvisoConservazione(posizione, metodo, frigoriferi, lotto?.locale_id);

  const cambiaPosizione = (value) => {
    setPosizione(value);
    if (!metodo && value) {
      const suggerito = metodoDaFrigo(frigoDellaPosizione(value, frigoriferi, lotto?.locale_id));
      if (suggerito) {
        setMetodo(suggerito);
        setFormCongela(prev => ({ ...prev, mesi: suggerito === "abbattitore" ? 6 : 3 }));
        setStep("dettagli");
      }
    }
  };

  const scegliMetodo = (next) => {
    setMetodo(next);
    setFormCongela(prev => ({ ...prev, mesi: next === "abbattitore" ? 6 : 3 }));
    if (!avvisoConservazione(posizione, next, frigoriferi, lotto?.locale_id)) setStep("dettagli");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto rounded-2xl" data-testid="post-creazione-lotto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base" data-testid="post-creazione-titolo">
            {step === "dettagli" ? nomeMetodo(metodo) : "Lotto creato"}
          </DialogTitle>
          <DialogDescription data-testid="post-creazione-prodotto">{nomeProdotto || "Il lotto"}</DialogDescription>
        </DialogHeader>

        {step === "scelta" ? (
          <div className="space-y-4" data-testid="post-creazione-scelta">
            <PosizioneFrigoSelect value={posizione} onChange={cambiaPosizione} frigoriferi={frigoriferi}
              posizioniLegacy={posizioni} testId="post-creazione-posizione" />
            <div className="grid grid-cols-1 gap-2" role="group" aria-label="Conservazione">
              {METODI.map(({ id, label, icon: Icon, color }) => (
                <button key={id} type="button" onClick={() => scegliMetodo(id)}
                  className={`flex w-full items-center gap-3 rounded-lg border-2 p-3 text-left transition-[background-color,border-color] ${color} ${metodo === id ? "ring-2 ring-offset-1 ring-current" : ""}`}
                  aria-pressed={metodo === id} data-testid={`btn-scegli-${id.replaceAll("_", "-")}`}>
                  <Icon className="h-5 w-5 shrink-0" /><span className="text-sm font-bold">{label}</span>
                </button>
              ))}
            </div>
            {avviso && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="post-creazione-metodo-avviso">{avviso}</p>}
            {metodo && <Button type="button" className="w-full" onClick={() => setStep("dettagli")} data-testid="post-creazione-continua">Continua{avviso ? " comunque" : ""}</Button>}
          </div>
        ) : (
          <div className="space-y-4" data-testid="post-creazione-dettagli">
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 text-sm font-semibold text-foreground" data-testid="post-creazione-riepilogo">
                {nomeMetodo(metodo)} · {frigo?.nome || (posizione ? "Posizione precedente" : "Posizione non impostata")}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => setStep("scelta")} data-testid="post-creazione-indietro">
                <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
              </Button>
            </div>
            {avviso && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950" data-testid="post-creazione-conferma-avviso">{avviso}</p>}
            {(metodo === "frigo" || metodo === "temp_ambiente") ? (
              <div className="space-y-3">
                {defaultScadenza ? (
                  <p className="text-sm text-muted-foreground" data-testid="post-creazione-scadenza-esistente">Scadenza già registrata: {new Date(`${defaultScadenza}T00:00:00`).toLocaleDateString("it-IT")}</p>
                ) : (
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold">Data di scadenza</label>
                    <DateInput value={dataScadenza} onChange={setDataScadenza} disablePast testId="post-creazione-scadenza" />
                  </div>
                )}
                <Button type="button" onClick={() => onConserva?.(defaultScadenza ? null : dataScadenza, posizione, metodo)} disabled={(!defaultScadenza && !dataScadenza) || saving}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-conferma-conservazione">
                  <Printer className="h-4 w-4 mr-2" /> {saving ? "Salvataggio..." : `Conferma ${nomeMetodo(metodo).toLowerCase()} e stampa`}
                </Button>
              </div>
            ) : (
              <PostCreazioneDatiCongelamento lotto={lotto} metodo={metodo} form={formCongela}
                setForm={setFormCongela} saving={saving}
                onConferma={values => onCongela?.({ ...values, posizione })} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PostCreazioneLottoDialog;